import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  Typography,
  alpha,
} from '@mui/material';
import {
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Link as LinkIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  AccessTime as TimeIcon,
  Schedule as ScheduleIcon,
  OpenInNew as OpenInNewIcon,
  Inventory2 as ItemIcon,
  Payments as PaymentsIcon,
  TrackChanges as PurposeIcon,
  Circle as CircleIcon,
  LocationOn as LocationIcon,
  AlternateEmail as EmailIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import Loading from '../../common/Loading';
import Header from '../../common/Header';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import SystemInformationSection from '../../common/SystemInformationSection';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import {
  canEditRequirement,
  canCancelRequirement,
  canAccessPartnershipManagement,
} from '../../../utils/partnershipPermissions';
import { resolveUserId, userDetailsPath } from '../../../utils/userDetailsLink';
import { injectDetailsPageStyles } from '../../common/DetailsPagePrimitives';

// â”€â”€â”€ Design tokens â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const NEON       = '#38BDF8';
const NEON2      = '#818CF8';
const NEON3      = '#34D399';
const AMBER      = '#FBBF24';
const CORAL      = '#F87171';
const SURFACE_MID = '#0a1628';
const SURFACE_UP  = '#0f1f38';
const SURFACE_TOP = '#152640';
const TEXT_BODY   = '#E2E8F0';
const TEXT_MUTED  = '#64748B';
const TEXT_SUB    = '#94A3B8';

const webHref = (u) => {
  if (!u) return null;
  const s = String(u).trim();
  if (!s) return null;
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

const prettifyStatus = (status) =>
  status?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || '-';

const formatAuditUser = (record, field) =>
  field === 'created'
    ? record?.user?.name || record?.creator?.name || record?.created_by_name || '-'
    : record?.last_updater?.name || record?.updated_by_name || '-';

const formatSystemDate = (value) =>
  value ? format(new Date(value), 'dd MMM yyyy, h:mm a') : null;

const STATUS_CONFIG = {
  pending:     { color: AMBER,  label: 'Pending' },
  approved:    { color: NEON3,  label: 'Approved' },
  rejected:    { color: CORAL,  label: 'Rejected' },
  in_progress: { color: NEON,   label: 'In Progress' },
  in_contact:  { color: NEON2,  label: 'In Contact' },
  not_started: { color: TEXT_SUB, label: 'Not Started' },
  completed:   { color: NEON3,  label: 'Completed' },
  cancelled:   { color: '#FB923C', label: 'Cancelled' },
  archived:    { color: '#94A3B8', label: 'Archived' },
};

/** Animated section wrapper */
const Section = ({ title, icon: Icon, accent = NEON, delay = 0, children }) => (
  <Box
    sx={{
      borderRadius: '16px',
      border: `1px solid ${alpha(accent, 0.14)}`,
      background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
      overflow: 'hidden',
      animation: `fadeUp 0.4s ease ${delay}s both`,
      '@keyframes fadeUp': {
        from: { opacity: 0, transform: 'translateY(10px)' },
        to:   { opacity: 1, transform: 'translateY(0)' },
      },
    }}
  >
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accent, 0.1)}`,
      background: alpha(accent, 0.04),
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        background: alpha(accent, 0.12),
        border: `1px solid ${alpha(accent, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 13, color: accent }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.07em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

/** Compact info row */
const InfoRow = ({ icon: Icon, label, value, href, onClick, color = NEON, iconColor, emptyLabel }) => {
  const display = value || emptyLabel;
  if (!display) return null;
  const isEmpty = !value && emptyLabel;
  const isInteractive = Boolean((href || onClick) && !isEmpty);

  const content = (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      py: 0.9, px: 1.2, borderRadius: '9px',
      transition: 'background 0.15s',
      cursor: isInteractive ? 'pointer' : 'default',
      '&:hover': isInteractive ? { background: alpha(color, 0.06) } : {},
    }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
        background: alpha(iconColor || color, isEmpty ? 0.05 : 0.1),
        border: `1px solid ${alpha(iconColor || color, isEmpty ? 0.1 : 0.18)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 14, color: isEmpty ? TEXT_MUTED : (iconColor || alpha(color, 0.85)) }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.65rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '0.88rem',
          color: isEmpty ? TEXT_MUTED : (isInteractive ? color : TEXT_BODY),
          fontStyle: isEmpty ? 'italic' : 'normal',
          fontFamily: "'DM Sans', sans-serif", fontWeight: isEmpty ? 400 : 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {display}
        </Typography>
      </Box>
      {href && !isEmpty && <OpenInNewIcon sx={{ fontSize: 12, color: alpha(color, 0.45), flexShrink: 0 }} />}
    </Box>
  );

  if (href && !isEmpty) {
    return <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'block' }}>{content}</a>;
  }
  if (onClick && !isEmpty) {
    return (
      <Box
        role="link"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(e);
          }
        }}
        sx={{ textDecoration: 'none', display: 'block' }}
      >
        {content}
      </Box>
    );
  }
  return content;
};

/** Large highlighted metric card */
const MetricCard = ({ label, value, sub, icon: Icon, color = NEON, sx = {}, onClick }) => (
  <Box
    role={onClick ? 'link' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={onClick ? (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick(e);
      }
    } : undefined}
    sx={{
    position: 'relative',
    borderRadius: '14px', px: 2.5, py: 2,
    background: `linear-gradient(145deg, ${alpha(color, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
    border: `1px solid ${alpha(color, 0.2)}`,
    boxShadow: `0 0 24px ${alpha(color, 0.07)}, inset 0 1px 0 ${alpha(color, 0.1)}`,
    overflow: 'hidden',
    transition: 'box-shadow 0.25s, border-color 0.25s',
    cursor: onClick ? 'pointer' : 'default',
    '&:hover': onClick ? {
      boxShadow: `0 0 36px ${alpha(color, 0.16)}, inset 0 1px 0 ${alpha(color, 0.18)}`,
      borderColor: alpha(color, 0.35),
    } : {
      boxShadow: `0 0 36px ${alpha(color, 0.16)}, inset 0 1px 0 ${alpha(color, 0.18)}`,
      borderColor: alpha(color, 0.35),
    },
    ...sx,
  }}>
    {/* bg glow */}
    <Box sx={{ position: 'absolute', top: -16, right: -16, width: 70, height: 70, borderRadius: '50%', background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: alpha(color, 0.75) }} />}
      <Typography sx={{ fontSize: '0.67rem', fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED, letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color, letterSpacing: 0.3, lineHeight: 1.15 }}>
      {value || '-'}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", mt: 0.4 }}>{sub}</Typography>}
  </Box>
);

// â”€â”€â”€ Main Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const RequirementDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [requirement, setRequirement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const canEditReq = requirement ? canEditRequirement(currentUser, requirement) : false;
  const canCancelReq = requirement ? canCancelRequirement(currentUser, requirement) : false;

  useEffect(() => { injectDetailsPageStyles(); }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    fetchRequirement();
  }, [currentUser, id]);

  const fetchRequirement = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/requirements/${id}`);
      setRequirement(response.data);
      setError(null);
    } catch {
      setError('Failed to load requirement details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequirement = async () => {
    try {
      const title = requirement?.wanted_item || 'Requirement';
      await api.post(`/requirements/${id}/archive`);
      setRequirement((prev) => (prev ? { ...prev, archived_at: new Date().toISOString() } : prev));
      setCancelDialogOpen(false);
      setSuccessMessage(`Requirement "${title}" has been archived.`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to archive requirement. Please try again.');
      setCancelDialogOpen(false);
    }
  };

  if (!currentUser || loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message="Loading requirement details" size={56} variant="primary" />
    </Box>
  );

  if (!requirement) return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <FeedbackMessage message={error || 'Requirement not found'} severity="error"
        sx={{ fontFamily: "'DM Sans', sans-serif", background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }}
      />
    </Container>
  );

  const effectiveStatus = requirement.archived_at ? 'archived' : requirement.status;
  const statusCfg = STATUS_CONFIG[effectiveStatus]
    ?? (effectiveStatus === 'archived'
      ? { color: '#FB923C', label: 'Archived' }
      : { color: TEXT_SUB, label: prettifyStatus(effectiveStatus) });
  const emergencyCfg = requirement.is_emergency
    ? { color: CORAL, label: 'Emergency' }
    : { color: TEXT_SUB, label: 'Not Emergency' };
  const assignee = requirement.responsible;
  const assigneeId = resolveUserId(assignee ?? requirement.responsible_id);
  const assigneeNames = (requirement.assignees || []).map((member) => member.name).filter(Boolean).join(', ');
  const submitterId = resolveUserId(requirement.user ?? requirement.creator);
  const linkedCompanyId = requirement.company_id ?? requirement.company?.id ?? null;
  const isLinkedCompany = Boolean(linkedCompanyId);
  const canViewCompanyLink = canAccessPartnershipManagement(currentUser);
  const canLinkToCompany = isLinkedCompany && canViewCompanyLink;
  const companyName = requirement.company?.name || requirement.company_name;
  const companyAddress = requirement.company?.location || requirement.company?.address || requirement.company_address;
  const companyContact = requirement.company?.contact_number || requirement.company?.phone || requirement.company_contact;
  const companyContactEmail = requirement.company?.gmail || requirement.company?.email || requirement.company_contact_email;
  const companyWebsite = requirement.company?.website_link || requirement.company?.website || requirement.company_website;

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 8 }}>

      {/* Header */}
      <Header
        title="Requirement Details"
        subtitle="View requirement information"
        icon={AssignmentIcon}
        showBackButton
        onBackClick={() => navigate(-1)}
        actionButton={
          (canEditReq || canCancelReq) && (
            <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
              {canEditReq && (
                <Button
                  startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                  onClick={() => navigate(`/requirements/edit/${id}`)}
                  sx={{
                    borderRadius: '10px', px: 2.2, py: 0.65,
                    fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem',
                    background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.55)}`,
                    color: NEON, transition: 'all 0.2s',
                    '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 14px ${alpha(NEON, 0.2)}` },
                  }}
                >Edit</Button>
              )}
              {canCancelReq && (
                <Button
                  startIcon={<ArchiveIcon sx={{ fontSize: 15 }} />}
                  onClick={() => setCancelDialogOpen(true)}
                  sx={{
                    borderRadius: '10px', px: 2.2, py: 0.65,
                    fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.83rem',
                    background: 'transparent', border: `1.5px solid ${alpha(CORAL, 0.55)}`,
                    color: CORAL, transition: 'all 0.2s',
                    '&:hover': { background: alpha(CORAL, 0.08), borderColor: CORAL, boxShadow: `0 0 14px ${alpha(CORAL, 0.2)}` },
                  }}
                >Archive</Button>
              )}
            </Box>
          )
        }
      />

      {/* â”€â”€ Feedback â”€â”€ */}
      {error && (
        <FeedbackMessage message={error} severity="error" onClose={() => setError(null)}
          sx={{ mb: 2.5, fontFamily: "'DM Sans', sans-serif", background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }}
        />
      )}
      {successMessage && (
        <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage(null)}
          sx={{ mb: 2.5, fontFamily: "'DM Sans', sans-serif", background: alpha(NEON3, 0.08), border: `1px solid ${alpha(NEON3, 0.3)}`, color: NEON3 }}
        />
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* HERO CARD */}
        <Box sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(NEON, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
          p: { xs: 2, md: 2.5 },
          animation: 'fadeUp 0.35s ease both',
          '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        }}>

          {/* Name row + emergency badge */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: '13px', flexShrink: 0,
                background: `linear-gradient(135deg, ${alpha(NEON2, 0.25)}, ${alpha(NEON, 0.15)})`,
                border: `2px solid ${alpha(NEON2, 0.3)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <AssignmentIcon sx={{ fontSize: 22, color: NEON2 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: TEXT_BODY, letterSpacing: 0.3, lineHeight: 1.15 }}>
                  {requirement.wanted_item || 'Untitled Request'}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
                  Submitted {dayjs(requirement.created_at).format('MMMM D, YYYY')}
                  {requirement.user?.name && (
                    <>
                      {' · by '}
                      {submitterId ? (
                        <Box
                          component="span"
                          role="link"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); navigate(userDetailsPath(submitterId)); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(userDetailsPath(submitterId));
                            }
                          }}
                          sx={{ color: NEON, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {requirement.user.name}
                        </Box>
                      ) : requirement.user.name}
                    </>
                  )}
                </Typography>
              </Box>
            </Box>

            {/* Emergency badge */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.7,
              px: 1.4, py: 0.5, borderRadius: '20px',
              background: alpha(emergencyCfg.color, 0.1),
              border: `1px solid ${alpha(emergencyCfg.color, 0.3)}`,
              alignSelf: 'flex-start',
            }}>
              <CircleIcon sx={{ fontSize: 7, color: emergencyCfg.color }} />
              <Typography sx={{ fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: emergencyCfg.color, letterSpacing: '0.05em' }}>
                {emergencyCfg.label}
              </Typography>
            </Box>
          </Box>

          {/* Metric strip */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            <MetricCard
              label="Requested Item"
              value={requirement.wanted_item}
              icon={ItemIcon}
              color={NEON}
            />
            <MetricCard
              label="Amount / Quantity"
              value={requirement.amount}
              icon={PaymentsIcon}
              color={NEON2}
            />
            <MetricCard
              label="Assigned To"
              value={assigneeNames || assignee?.name || null}
              sub={(assigneeNames || assignee) ? 'Handling this request' : undefined}
              icon={PersonIcon}
              color={assignee ? NEON3 : TEXT_MUTED}
              onClick={assigneeId ? () => navigate(userDetailsPath(assigneeId)) : undefined}
            />
            <MetricCard
              label="Status"
              value={statusCfg.label}
              icon={AssignmentIcon}
              color={statusCfg.color}
            />
          </Box>
        </Box>

        {/* PURPOSE */}
        {requirement.purpose && (
          <Section title="Purpose" icon={PurposeIcon} delay={0.08}>
            <Box sx={{
              px: 2, py: 1.8, borderRadius: '10px',
              borderLeft: `3px solid ${alpha(NEON, 0.4)}`,
              background: alpha(SURFACE_TOP, 0.4),
            }}>
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.92rem', color: TEXT_BODY, lineHeight: 1.75 }}>
                {requirement.purpose}
              </Typography>
            </Box>
          </Section>
        )}

        {/* â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•
            Related Company (conditional)
            â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â• */}
        {(companyName || isLinkedCompany) && (
          <Section
            title={isLinkedCompany ? 'Related Company' : 'Related Company (Manual Entry)'}
            icon={BusinessIcon}
            accent={NEON2}
            delay={0.15}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 0.3 }}>
              <InfoRow
                icon={BusinessIcon}
                label="Company Name"
                value={companyName}
                color={NEON2}
                onClick={canLinkToCompany ? () => navigate(`/companies/${linkedCompanyId}`) : undefined}
              />
              <InfoRow icon={LocationIcon} label="Address" value={companyAddress} color={NEON2} />
              <InfoRow icon={PhoneIcon} label="Contact" value={companyContact} color={NEON2} />
              <InfoRow icon={EmailIcon} label="Contact Email" value={companyContactEmail} color={NEON2} />
              <InfoRow
                icon={LinkIcon}
                label="Website"
                value={companyWebsite}
                href={webHref(companyWebsite)}
                color={NEON2}
              />
            </Box>
          </Section>
        )}
        <SystemInformationSection
          createdBy={formatAuditUser(requirement, 'created')}
          createdAt={formatSystemDate(requirement.created_at)}
          updatedBy={formatAuditUser(requirement, 'updated')}
          updatedAt={formatSystemDate(requirement.updated_at)}
          delay={0.22}
          accentColor={NEON}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />

      </Box>

      <ConfirmDeleteDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelRequirement}
        title="Archive Requirement"
        itemType="requirement"
        itemName={requirement?.wanted_item}
        description="The requirement will be archived and remain in the system for records."
        confirmLabel="Archive Requirement"
        actionVerb="archive"
      />

    </Container>
  );
};

export default RequirementDetails;
